"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import Link from "next/link";
import { BrandWordmark, BrandXMark } from "@/components/icons/SiteMarks";
import { useContent } from "@/components/site/ContentProvider";
import {
  homeSectionHref,
  localeHref,
  localePath,
} from "@/content/index";
import {
  acquireSubpageDocument,
  releaseSubpageDocument,
} from "@/components/pages/subpage-document";
import styles from "./subpage.module.css";

const SHOW_LOCALE_SWITCHER = true;

type SubpageChromeProps = {
  children: ReactNode;
  /** Active marketing path for locale pair (`services` | `work`). */
  path: "services" | "work";
};

function navHref(
  locale: "en" | "ar",
  section: string,
): string {
  // Founder rule: off-home Work/Services → real pages; other labels → home spine.
  if (section === "work") return localeHref(locale, "work");
  if (section === "services") return localeHref(locale, "services");
  return homeSectionHref(locale, section);
}

/**
 * Document chrome for `/en|ar/services` and `/en|ar/work` (ADR-020).
 * Off-home: Work / Services → locale document pages; Products / Story
 * still deep-link via `homeSectionHref`. CTA scrolls to on-page `#inquire`.
 * Homepage JumpMenu stays spine seeks only.
 */
export function SubpageChrome({ children, path }: SubpageChromeProps) {
  const site = useContent();
  const home = localePath(site.locale);
  const navId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const localePair =
    site.locale === "en"
      ? localeHref("ar", path)
      : localeHref("en", path);

  /* useLayoutEffect: before paint. Refcount: Work↔Services soft-nav must
   * not drop `html.subpage` for a frame (spine fixed header + blur ghosts
   * the hero headline as a doubled/offset copy). */
  useLayoutEffect(() => {
    acquireSubpageDocument();
    return () => releaseSubpageDocument();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 601px)").matches) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scrollToInquire = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMenuOpen(false);
    document
      .getElementById("inquire")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const menuLabel = site.locale === "ar" ? (menuOpen ? "إغلاق" : "القائمة") : menuOpen ? "Close" : "Menu";

  return (
    <div className={styles.root} data-subpage={path}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerSide}>
            <a href={home} className={styles.logo} aria-label={site.aria.home}>
              <BrandWordmark className={styles.logoMark} />
            </a>
            <button
              type="button"
              className={styles.menuToggle}
              aria-expanded={menuOpen}
              aria-controls={navId}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className={styles.menuToggleBars} aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <strong className={`label ${styles.menuToggleLabel}`}>{menuLabel}</strong>
            </button>
            <nav
              id={navId}
              className={[styles.nav, menuOpen ? styles.navOpen : ""].filter(Boolean).join(" ")}
              aria-label="Primary"
            >
              <ul className={styles.navList}>
                {site.nav.map((item) => {
                  const href = navHref(site.locale, item.section);
                  const isCurrent =
                    (path === "work" && item.section === "work") ||
                    (path === "services" && item.section === "services");
                  const softNav =
                    item.section === "work" || item.section === "services";
                  const className = [
                    styles.navLink,
                    isCurrent ? styles.navLinkCurrent : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const inner = (
                    <>
                      <BrandXMark className={styles.navX} />
                      <strong className="label">{item.label}</strong>
                      <BrandXMark className={styles.navX} />
                    </>
                  );
                  return (
                    <li key={item.section}>
                      {softNav ? (
                        <Link
                          href={href}
                          className={className}
                          aria-current={isCurrent ? "page" : undefined}
                          onClick={() => setMenuOpen(false)}
                        >
                          {inner}
                        </Link>
                      ) : (
                        <a
                          href={href}
                          className={className}
                          aria-current={isCurrent ? "page" : undefined}
                          onClick={() => setMenuOpen(false)}
                        >
                          {inner}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
          <div className={styles.headerActions}>
            {SHOW_LOCALE_SWITCHER ? (
              <a
                href={localePair}
                className={`site_btn-like f a-c j-c ${styles.locale}`}
                aria-label={site.localeSwitch.aria}
              >
                <strong className="label">{site.localeSwitch.label}</strong>
              </a>
            ) : null}
            <a
              href="#inquire"
              className={`site_btn f a-c j-c ${styles.cta}`}
              onClick={scrollToInquire}
            >
              <strong className="label">{site.cta.label}</strong>
            </a>
          </div>
        </div>
      </header>
      <main className={styles.main}>{children}</main>
    </div>
  );
}
